/**
 * Ghostwriter primary CTA button for ViewJob.
 *
 * Renders the appropriate button based on the computed
 * GhostwriterButtonAction from useGhostwriterForViewJob.
 * Returns null when no ghostwriter button should be shown.
 *
 * Fades in with a subtle upward slide so the button doesn't
 * pop in abruptly after the ViewJob data loads.
 */

import type { GhostwriterButtonAction } from "../context/useGhostwriterForViewJob";

import { AIGeneral, Button, Spinner } from "../../../mads";
import { useTheme } from "../../../mads/ThemeProvider";
import { useI18n } from "../../I18n";

import React, { useEffect, useRef } from "react";
import { Animated } from "react-native";

const FADE_DURATION_MS = 250;
const SLIDE_DISTANCE = 12;

export interface GhostwriterApplyButtonProps {
  buttonAction: GhostwriterButtonAction;
  onTryAutoApply: () => void;
  onAutoApply: () => void;
  onReviewApplication: () => void;
  useProgressLoadingButton: boolean;
  onCheckStatusInMyJobs: () => void;
  /**
   * When true, the button renders the "Prefill" copy variants (sync_prefill_submit
   * per RN-3662, and its hybrid_automation clone per RN-3367): "Prefill your
   * application" instead of the "Try auto apply" / "Auto apply" labels,
   * and "Check application status" instead of the "Auto apply in
   * progress" / "Review application" labels. Other states fall back to
   * the async_auto_apply_submit-bucket copy.
   */
  isSyncPrefill?: boolean;
  /**
   * Animated value controlling the fade/slide-in. When provided by the parent,
   * the value persists across remounts so the animation doesn't replay on
   * transient ghostwriter state changes.
   */
  animValue?: Animated.Value;
  isLoading?: boolean;
}

export function GhostwriterApplyButton({
  buttonAction,
  onTryAutoApply,
  onAutoApply,
  onReviewApplication,
  useProgressLoadingButton,
  onCheckStatusInMyJobs,
  isSyncPrefill = false,
  animValue,
  isLoading = false,
}: GhostwriterApplyButtonProps): React.JSX.Element | null {
  const i18n = useI18n();
  const theme = useTheme();
  const internalAnim = useRef(new Animated.Value(0)).current;
  const anim = animValue ?? internalAnim;

  const isVisible =
    buttonAction === "try-auto-apply" ||
    buttonAction === "auto-apply" ||
    buttonAction === "in-progress" ||
    buttonAction === "ready-for-review" ||
    buttonAction === "applied";

  useEffect(() => {
    if (isVisible) {
      Animated.timing(anim, {
        toValue: 1,
        duration: FADE_DURATION_MS,
        useNativeDriver: true,
      }).start();
    }
  }, [isVisible, anim]);

  if (!isVisible) return null;

  const animatedStyle = {
    opacity: anim,
    transform: [
      {
        translateY: anim.interpolate({
          inputRange: [0, 1],
          outputRange: [SLIDE_DISTANCE, 0],
        }),
      },
    ],
  };

  let content: React.JSX.Element;

  // sync_prefill_submit (synchronous flow) collapses the in-progress / review
  // copy into a single "Check application status" CTA, and the entry
  // CTAs share a single "Prefill your application" label regardless
  // of whether the user has consented yet — see RN-3662.
  const checkStatusLabel = isSyncPrefill
    ? i18n.gettext("Check application status")
    : i18n.gettext("Check status in My Jobs");
  const reviewLabel = isSyncPrefill
    ? i18n.gettext("Check application status")
    : i18n.gettext("Review application");
  const inProgressLabel = isSyncPrefill
    ? i18n.gettext("Check application status")
    : i18n.gettext("Auto apply in progress");
  const tryAutoApplyLabel = isSyncPrefill
    ? i18n.gettext("Prefill your application")
    : i18n.gettext("Try auto apply");
  const autoApplyLabel = isSyncPrefill
    ? i18n.gettext("Prefill your application")
    : i18n.gettext("Auto apply");

  switch (buttonAction) {
    case "try-auto-apply":
      content = (
        <Button
          testID="ghostwriter-try-auto-apply"
          variant="primary"
          size="lg"
          onPress={onTryAutoApply}
          loading={isLoading}
          iconAfter={
            isLoading ? undefined : <AIGeneral color={theme.colors.foregroundActionInverse} />
          }
        >
          {tryAutoApplyLabel}
        </Button>
      );
      break;

    case "auto-apply":
      content = (
        <Button
          testID="ghostwriter-auto-apply"
          variant="primary"
          size="lg"
          onPress={onAutoApply}
          loading={isLoading}
          iconAfter={
            isLoading ? undefined : <AIGeneral color={theme.colors.foregroundActionInverse} />
          }
        >
          {autoApplyLabel}
        </Button>
      );
      break;

    case "in-progress":
      content = useProgressLoadingButton ? (
        <Button
          testID="ghostwriter-auto-apply-in-progress"
          variant="primary"
          size="lg"
          disabled
          onPress={() => {}}
          iconAfter={<Spinner inverse />}
        >
          {inProgressLabel}
        </Button>
      ) : (
        <Button
          testID="ghostwriter-check-status-in-my-jobs"
          variant="primary"
          size="lg"
          onPress={onCheckStatusInMyJobs}
        >
          {checkStatusLabel}
        </Button>
      );
      break;

    case "ready-for-review":
      content = (
        <Button
          testID="ghostwriter-ready-for-review"
          variant="primary"
          size="lg"
          onPress={onReviewApplication}
        >
          {reviewLabel}
        </Button>
      );
      break;

    case "applied":
      content = (
        <Button
          testID="ghostwriter-applied"
          variant="primary"
          size="lg"
          disabled
          onPress={() => {}}
        >
          {i18n.gettext("Applied")}
        </Button>
      );
      break;
  }

  return <Animated.View style={animatedStyle}>{content}</Animated.View>;
}
